import { JSX, useEffect, useMemo, useRef } from 'react';
import { Control, useWatch } from 'react-hook-form';

import {
  LTLItem,
  QuickQuoteInputs,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { Maybe, Undef } from 'types/UtilityTypes';
import { cn } from 'utils/shadcn';

// LTL item-card hooks for side-load status tags and split-row edit behavior.
interface UseAutoOpenSplitSideLoadRowParams {
  watchedItem: Undef<Pick<LTLItem, 'itemId' | 'parentItemId'>>;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
}

// Split-row UX: inactive while allowSplitMerge=false; kept for Tai/Turvo/ThreeG parity.
export function useAutoOpenSplitSideLoadRow({
  watchedItem,
  isEditing,
  setIsEditing,
}: UseAutoOpenSplitSideLoadRowParams): void {
  const hasAutoOpenedCurrentSplitRef = useRef(false);
  const lastAutoOpenedSplitRowKeyRef = useRef<Maybe<string>>(null);

  useEffect(() => {
    const isSplitRow =
      watchedItem?.parentItemId != null &&
      watchedItem.parentItemId !== watchedItem.itemId;

    if (!isSplitRow) {
      hasAutoOpenedCurrentSplitRef.current = false;
      lastAutoOpenedSplitRowKeyRef.current = null;
      return;
    }

    const splitRowKey = `${watchedItem.parentItemId}:${watchedItem.itemId}`;
    if (lastAutoOpenedSplitRowKeyRef.current !== splitRowKey) {
      hasAutoOpenedCurrentSplitRef.current = false;
      lastAutoOpenedSplitRowKeyRef.current = splitRowKey;
    }

    if (!isEditing && !hasAutoOpenedCurrentSplitRef.current) {
      setIsEditing(true);
      hasAutoOpenedCurrentSplitRef.current = true;
    }
  }, [isEditing, setIsEditing, watchedItem?.itemId, watchedItem?.parentItemId]);
}

interface UseSideLoadItemStatusTagParams {
  control: Control<QuickQuoteInputs>;
  itemIndex: number;
  parentItemLabelPrefix?: string;
}

interface SideLoadStatusTag {
  label: string;
  className: string;
}

interface UseSideLoadItemStatusTagResult {
  sideLoadStatusTag: Maybe<SideLoadStatusTag>;
  renderSideLoadStatusTag: (maxWidthClassName?: string) => Maybe<JSX.Element>;
}

export function useSideLoadItemStatusTag({
  control,
  itemIndex,
  parentItemLabelPrefix = 'Commodity',
}: UseSideLoadItemStatusTagParams): UseSideLoadItemStatusTagResult {
  const sideLoadEnabled =
    useWatch({ control, name: 'ltl.commodity.sideLoadEnabled' }) ?? false;
  const items = useWatch({ control, name: 'ltl.items' }) ?? [];
  const watchedItem = items[itemIndex];

  const sideLoadStatusTag = useMemo((): Maybe<SideLoadStatusTag> => {
    if (!sideLoadEnabled || !watchedItem) {
      return null;
    }

    // Split/merge is off in production (allowSplitMerge=false) so this tag is inactive
    // today; kept for Tai/Turvo/ThreeG parity when split/merge ships behind the same gate.
    if (
      watchedItem.parentItemId != null &&
      watchedItem.parentItemId !== watchedItem.itemId
    ) {
      const parentIndex = items.findIndex(
        (item) => item.itemId === watchedItem.parentItemId
      );
      const parentDescription =
        parentIndex >= 0 ? items[parentIndex]?.description?.trim() : '';
      const parentLabel =
        parentDescription ||
        (parentIndex >= 0
          ? `${parentItemLabelPrefix} ${parentIndex + 1}`
          : `parent ${parentItemLabelPrefix.toLowerCase()}`);
      return {
        label: `Split from ${parentLabel}`,
        className: 'bg-warning-200 text-warning-800 border-warning-400',
      };
    }

    if (
      watchedItem.parentItemId != null &&
      watchedItem.parentItemId === watchedItem.itemId
    ) {
      return {
        label: 'Rotated',
        className: 'bg-info-100 text-info-700 border-info-300',
      };
    }

    return null;
  }, [items, parentItemLabelPrefix, sideLoadEnabled, watchedItem]);

  const renderSideLoadStatusTag = (
    maxWidthClassName = 'max-w-[160px]'
  ): Maybe<JSX.Element> => {
    if (!sideLoadStatusTag) {
      return null;
    }

    return (
      <span
        title={sideLoadStatusTag.label}
        className={cn(
          'inline-flex min-w-0 items-center overflow-hidden rounded-full border px-2 py-1 text-[11px] font-medium leading-4',
          maxWidthClassName,
          sideLoadStatusTag.className
        )}
      >
        <span className='truncate'>{sideLoadStatusTag.label}</span>
      </span>
    );
  };

  const result: UseSideLoadItemStatusTagResult = {
    sideLoadStatusTag,
    renderSideLoadStatusTag,
  };

  return result;
}
